/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  Hidden,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Pagination,
  Stack,
  Switch,
  Grid,
  Typography,
  Theme,
  Skeleton,
} from '@mui/material';
import { ChangeEvent } from 'react';
import { SxProps } from '@mui/system';

export type ColumnType = {
  /**
   * Header of the column
   */
  header: string | (() => React.ReactNode);

  /**
   * To get the value from the row
   */
  label?: string;

  /**
   * Value to render in the table
   */
  value?: string | number | boolean | undefined;

  /**
   * Function to get the value from the row
   */
  valueGetter?: (row: any) => string | number | boolean | undefined;

  /**
   * Boolean to check if the cell is switch
   */
  isSwitch?: boolean;

  /**
   * Action for switch component
   */
  switchAction?: (id: string) => void;

  /**
   * Render custom cell
   */
  renderCell?: (row: any) => React.ReactNode;
  renderEditDeleteCell?: (row: any, index: number, groupIndex?: number) => React.ReactNode;
  isVisibleInMobileView?: (row: any) => boolean;
};

const DataRow = ({
  row,
  index,
  rowCustomStyles,
  groupIndex,
  currentPage,
  tableHeader,
}: {
  row: any;
  index: number;
  rowCustomStyles?: SxProps<Theme>;
  groupIndex?: number;
  currentPage?: number;
  tableHeader: ColumnType[];
}): JSX.Element => {
  return (
    <TableRow
      key={index}
      aria-rowindex={index + 2}
      sx={{
        background: currentPage ? '#FAFAFB' : '',
        ':hover': {
          bgcolor: currentPage ? '#FAFAFB' : '',
        },
        ...rowCustomStyles,
      }}>
      {tableHeader.map((header, ind) => {
        const { valueGetter, isSwitch, renderCell, label = '', renderEditDeleteCell } = header;
        const isFirstCell = ind === 0;
        return (
          <TableCell
            key={ind}
            align="center"
            component={isFirstCell ? 'th' : 'td'}
            scope={isFirstCell ? 'row' : undefined}
            /* {isSwitch ? (
              <Stack alignItems={'center'}>
                <Switch
                  sx={{ '.MuiSwitch-switchBase': { color: 'black' } }}
                  checked={row.isActive}
                  // onChange={() => header.switchAction && header.switchAction(row.id)}
                />
              </Stack>
            ) :  */
          >
            {valueGetter
              ? valueGetter(row)
              : renderCell
              ? renderCell(row)
              : renderEditDeleteCell
              ? renderEditDeleteCell(row, index, groupIndex)
              : row[label]}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export const MobileViewDataRow = ({
  row,
  index,
  groupIndex,
  tableHeader,
  isLoading = false,
}: {
  row: any;
  index: number;
  groupIndex?: number;
  tableHeader: ColumnType[];
  isLoading?: boolean;
}): JSX.Element => {
  return (
    <Grid
      key={index}
      container
      // columnSpacing={1}
      sx={{
        border: '1px solid #00D9DA',
        p: 1.25,
        // ml: 0,
        borderRadius: 1.25,
        mb: 1.25,
        rowGap: 1.25,
        padding: { xs: '10px', sm: '55px' },
        '.MuiGrid-item': {
          px: 1,
        },
        '.MuiTypography-root': {
          fontSize: { xs: 10, sm: 16 },
          fontWeight: 500,
          maxWidth: '204px',
          overflowWrap: 'anywhere',
        },
        '& .MuiTypography-h6': {
          fontFamily: 'Poppins, sans-serif',
          color: 'primary.main',
        },
        '& .MuiTypography-body1': {
          color: 'rgba(41, 49, 57, 0.7)',
        },
      }}>
      {tableHeader.map((headerList, idx) => {
        const {
          valueGetter,
          isSwitch,
          label = '',
          renderCell,
          header,
          renderEditDeleteCell,
          isVisibleInMobileView,
        } = headerList;
        return (
          (isVisibleInMobileView ? isVisibleInMobileView(row) : true) && (
            <Grid item xs={6} sm={4} key={idx}>
              {typeof header !== 'string' ? (
                header()
              ) : (
                <Typography variant="h6">{headerList.header}</Typography>
              )}
              {isLoading ? (
                <Skeleton />
              ) : // isSwitch ? (
              //   <Stack
              //     sx={{
              //       '.MuiSwitch-root': { paddingLeft: '0px', width: '52px' },
              //       '.MuiSwitch-switchBase': { paddingLeft: '0px' },
              //     }}>
              //     <Switch
              //       sx={{
              //         '.MuiSwitch-switchBase': { color: 'black' },
              //       }}
              //       checked={row[label]}
              //       onChange={() => headerList.switchAction && headerList.switchAction(row.id)}
              //     />
              //   </Stack>
              // ) :
              renderCell ? (
                renderCell(row)
              ) : renderEditDeleteCell ? (
                renderEditDeleteCell(row, index, groupIndex)
              ) : (
                <Typography>{valueGetter ? valueGetter(row) : row[label]}</Typography>
              )}
            </Grid>
          )
        );
      })}
    </Grid>
  );
};

export const WebViewDataRow = ({
  rowCustomStyles,
  renderAdditionalRow = false,
  tableBodyCustomStyles,
  tableHeaderCustomStyles,
  groupIndex,
  currentPage,
  tableHeader,
  tableData,
  tableAriaLabel = 'Application table',
}: {
  rowCustomStyles?: SxProps<Theme>;
  currentPage?: number;
  renderAdditionalRow?: boolean;
  tableBodyCustomStyles?: SxProps<Theme>;
  tableHeaderCustomStyles?: SxProps<Theme>;
  groupIndex?: number;
  tableHeader: ColumnType[];
  tableData: any;
  tableAriaLabel?: string;
}): JSX.Element => {
  const totalRows = tableData.length ? tableData.length + 1 : 1;
  const totalCols = tableHeader.length;

  return (
    <TableContainer sx={{ bgcolor: 'common.white', overflowX: 'auto', width: '100%' }}>
      <Table aria-label={tableAriaLabel} aria-rowcount={totalRows} aria-colcount={totalCols}>
        <TableHead>
          <TableRow
            aria-rowindex={1}
            sx={{
              '.MuiTableCell-head': {
                fontFamily: 'Poppins, sans-serif',
                fontSize: 12,
                color: 'primary.main',
              },
              ...tableHeaderCustomStyles,
            }}>
            {tableHeader.map((currHeader: ColumnType, index: number) => {
              const { header } = currHeader;
              const headerContent = typeof header !== 'string' ? header() : currHeader['header'];
              return (
                <TableCell
                  component="th"
                  scope="col"
                  key={'h' + index}
                  align="center"
                  sx={{
                    whiteSpace: 'pre-line',
                  }}>
                  {headerContent || (
                    <Typography
                      component="span"
                      sx={{
                        clip: 'rect(0 0 0 0)',
                        clipPath: 'inset(50%)',
                        height: 1,
                        overflow: 'hidden',
                        position: 'absolute',
                        whiteSpace: 'nowrap',
                        width: 1,
                      }}>
                      Actions
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            '.MuiTableRow-root': {
              cursor: currentPage ? 'pointer' : 'auto',
              ':hover': {
                bgcolor: currentPage ? '#F4FCFC' : '',
              },
              ...(renderAdditionalRow && {
                '&:not(:last-of-type)': {
                  borderBottom: '8px solid transparent',
                },
              }),
            },
            ...tableBodyCustomStyles,
          }}>
          {tableData.length ? (
            tableData.map((row: any, index: number) => (
              <React.Fragment key={index}>
                <DataRow
                  groupIndex={groupIndex}
                  index={index}
                  row={row}
                  key={index}
                  rowCustomStyles={rowCustomStyles}
                  currentPage={currentPage}
                  tableHeader={tableHeader}
                />
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={tableHeader.length}
                role="status"
                aria-live="polite"
                sx={{
                  textAlign: 'center',
                  height: 100,
                }}>
                No data found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export function DataTable({
  isLoading,
  tableData,
  tableHeader,
  rowCustomStyles,
  noOfPages,
  currentPage,
  paginationAction,
  renderAdditionalRow = false,
  tableBodyCustomStyles,
  tableHeaderCustomStyles,
  groupIndex,
  boxStyles,
  tableAriaLabel,
}: {
  isLoading?: boolean;
  tableData: any;
  tableHeader: ColumnType[];
  rowCustomStyles?: SxProps<Theme>;
  noOfPages?: number;
  currentPage?: number;
  paginationAction?: (event: ChangeEvent<unknown>, value: number) => void;
  renderAdditionalRow?: boolean;
  tableBodyCustomStyles?: SxProps<Theme>;
  tableHeaderCustomStyles?: SxProps<Theme>;
  groupIndex?: number;
  boxStyles?: SxProps<Theme>;
  tableAriaLabel?: string;
}): JSX.Element {
  const isNoData = !isLoading && (!tableData || tableData.length === 0);
  tableData = isLoading ? new Array(5).fill({}) : tableData;
  return (
    <>
      {/* WCAG 4.1.3: Persistent ARIA live status region — announces empty state or search status changes to screen readers */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          border: 0,
        }}>
        {isNoData ? 'No data found' : ''}
      </div>
      <Hidden only="xs">
        <Box
          sx={{
            maxHeight: 'calc(100vh - 400px)',
            '@media (max-height: 700px)': {
              maxHeight: 'none',
            },
            overflowY: 'auto',
            minHeight: '480px',
            maxWidth: '100%',
            ...boxStyles,
          }}>
          {!isLoading ? (
            <WebViewDataRow
              rowCustomStyles={rowCustomStyles}
              currentPage={currentPage}
              renderAdditionalRow={renderAdditionalRow}
              tableBodyCustomStyles={tableBodyCustomStyles}
              tableHeaderCustomStyles={tableHeaderCustomStyles}
              groupIndex={groupIndex}
              tableHeader={tableHeader}
              tableData={tableData}
              tableAriaLabel={tableAriaLabel}
            />
          ) : (
            <LinearProgress />
          )}
        </Box>
      </Hidden>
      <Hidden smUp={true}>
        {tableData.map((row: any, index: number) => (
          <MobileViewDataRow
            index={index}
            row={row}
            key={index}
            tableHeader={tableHeader}
            isLoading={isLoading}
          />
        ))}
      </Hidden>
      {!!tableData.length && !!currentPage && !isLoading && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 5 }}>
          <Pagination
            count={noOfPages}
            page={currentPage}
            color="primary"
            onChange={paginationAction}
          />
        </Stack>
      )}
    </>
  );
}
